import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { Loading } from "@/components/bi/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyPairsEditor } from "@/components/bi/KeyPairsEditor";
import {
  Save, Plus, Trash2, Sigma, Tags, ListTree, ShieldCheck, PlayCircle, Loader2,
  CheckCircle2, AlertCircle, Calculator, Hash, ArrowLeft, GripVertical, Link2, Wand2, EyeOff,
} from "lucide-react";

const AGG_OPTIONS = [
  { value: "sum", label: "Sum" }, { value: "avg", label: "Average" },
  { value: "count", label: "Count" }, { value: "count_distinct", label: "Count distinct" },
  { value: "min", label: "Min" }, { value: "max", label: "Max" },
  { value: "median", label: "Median" }, { value: "p25", label: "25th pct" }, { value: "p75", label: "75th pct" },
];
const FORMAT_TYPES = [{ value: "number", label: "Number" }, { value: "currency", label: "Currency" }, { value: "percent", label: "Percent" }];
const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "AED", "SGD"];
const ROLES = [{ value: "*", label: "All roles" }, { value: "admin", label: "Admin" }, { value: "member", label: "Member" }];
const OPS = [{ value: "in", label: "in" }, { value: "not_in", label: "not in" }, { value: "eq", label: "equals" }, { value: "neq", label: "not equals" }];
const CUR_SYMBOL = { USD: "$", EUR: "€", GBP: "£", INR: "₹", JPY: "¥", AED: "AED ", SGD: "S$" };

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 7)}`;
const defFormat = () => ({ type: "number", decimals: 0, currency: "USD", compact: true, prefix: "", suffix: "" });

function compactNum(n, dec = 1) {
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(dec) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(dec) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(dec) + "K";
  return n.toLocaleString(undefined, { maximumFractionDigits: dec });
}
export function formatValue(v, f) {
  if (v === null || v === undefined || v === "") return "\u2014";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const dec = f?.decimals ?? 0;
  let s;
  if (f?.type === "percent") s = (n * 100).toFixed(dec) + "%";
  else if (f?.compact && Math.abs(n) >= 1000) s = compactNum(n, dec || 1);
  else s = n.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
  if (f?.type === "currency") s = (CUR_SYMBOL[f.currency] || "") + s;
  return (f?.prefix || "") + s + (f?.suffix || "");
}

export default function ModelStudio() {
  const { id } = useParams();
  const nav = useNavigate();
  const [model, setModel] = useState(null);
  const [schema, setSchema] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/datasets/${id}/model`);
        setSchema(data.dataset_schema || []);
        setModel(stripServer(data));
      } catch (e) { toast.error("Could not load model"); }
    })();
  }, [id]);

  const numericFields = useMemo(() => schema.filter((c) => c.type === "number"), [schema]);

  const patch = (key, val) => setModel((m) => ({ ...m, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/datasets/${id}/model`, model);
      setModel(stripServer(data));
      toast.success("Model saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!model) return <AppShell title="Modeling Studio"><Loading label="Loading semantic model" /></AppShell>;

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" className="bg-[color:var(--surface)]" onClick={() => nav(`/explore/${id}`)} data-testid="model-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Explore</Button>
      <Button data-testid="model-save-btn" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] hover:bg-[#0a6f9c] text-white">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save model
      </Button>
    </div>
  );

  return (
    <AppShell title={model.name || "Modeling Studio"} actions={actions}>
      <div className="px-5 md:px-8 py-6 max-w-[1200px]">
        <div className="glass-card p-4 mb-5 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-[color:var(--ink-3)]">Model name</Label>
            <Input data-testid="model-name" value={model.name} onChange={(e) => patch("name", e.target.value)} className="mt-1.5 bg-[color:var(--surface)] font-display" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-[color:var(--ink-3)]">Primary time dimension</Label>
            <Select value={model.time_dimension || "__none"} onValueChange={(v) => patch("time_dimension", v === "__none" ? null : v)}>
              <SelectTrigger className="mt-1.5 bg-[color:var(--surface)]" data-testid="model-time-dim"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {schema.filter((c) => c.type === "datetime").map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-4 text-center">
            <Stat label="Measures" value={model.measures.length} />
            <Stat label="Dimensions" value={model.dimensions.length} />
            <Stat label="Hierarchies" value={model.hierarchies.length} />
          </div>
        </div>

        <Tabs defaultValue={["measures", "dimensions", "hierarchies", "security", "joins", "preview"].includes(new URLSearchParams(window.location.search).get("tab")) ? new URLSearchParams(window.location.search).get("tab") : "measures"}>
          <TabsList className="bg-[color:var(--surface-2)]">
            <TabsTrigger value="measures" data-testid="tab-measures"><Sigma className="w-4 h-4 mr-1.5" />Measures</TabsTrigger>
            <TabsTrigger value="dimensions" data-testid="tab-dimensions"><Tags className="w-4 h-4 mr-1.5" />Dimensions</TabsTrigger>
            <TabsTrigger value="hierarchies" data-testid="tab-hierarchies"><ListTree className="w-4 h-4 mr-1.5" />Hierarchies</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck className="w-4 h-4 mr-1.5" />Security</TabsTrigger>
            <TabsTrigger value="joins" data-testid="tab-joins"><Link2 className="w-4 h-4 mr-1.5" />Joins</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview"><PlayCircle className="w-4 h-4 mr-1.5" />Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="measures" className="mt-4">
            <MeasuresTab model={model} setModel={setModel} numericFields={numericFields} datasetId={id} />
          </TabsContent>
          <TabsContent value="dimensions" className="mt-4">
            <DimensionsTab model={model} setModel={setModel} schema={schema} />
          </TabsContent>
          <TabsContent value="hierarchies" className="mt-4">
            <HierarchiesTab model={model} setModel={setModel} />
          </TabsContent>
          <TabsContent value="security" className="mt-4">
            <SecurityTab model={model} setModel={setModel} schema={schema} />
          </TabsContent>
          <TabsContent value="joins" className="mt-4">
            <JoinsTab datasetId={id} datasetSchema={schema} />
          </TabsContent>
          <TabsContent value="preview" className="mt-4">
            <PreviewTab model={model} datasetId={id} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function stripServer(d) {
  const { id, dataset_id, user_id, tenant_id, version, created_at, updated_at, dataset_schema, ...rest } = d;
  return {
    name: rest.name || "Model", description: rest.description || "", time_dimension: rest.time_dimension || null,
    dimensions: rest.dimensions || [], measures: rest.measures || [], hierarchies: rest.hierarchies || [],
    rls_rules: rest.rls_rules || [], cls_rules: rest.cls_rules || [], relationships: rest.relationships || [], field_meta: rest.field_meta || {},
  };
}

const Stat = ({ label, value }) => (
  <div className="px-3"><div className="font-display text-2xl text-[color:var(--ink)] tabular-nums">{value}</div><div className="text-[11px] text-[color:var(--ink-3)]">{label}</div></div>
);

function FormatEditor({ format, onChange }) {
  const f = format || defFormat();
  const set = (k, v) => onChange({ ...f, [k]: v });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={f.type} onValueChange={(v) => set("type", v)}>
        <SelectTrigger className="bg-[color:var(--surface)] w-[120px] h-8 text-xs" data-testid="fmt-type"><SelectValue /></SelectTrigger>
        <SelectContent>{FORMAT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
      </Select>
      {f.type === "currency" && (
        <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
          <SelectTrigger className="bg-[color:var(--surface)] w-[84px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <div className="flex items-center gap-1">
        <span className="text-[11px] text-[color:var(--ink-3)]">dec</span>
        <Input type="number" min={0} max={6} value={f.decimals} onChange={(e) => set("decimals", Number(e.target.value))} className="bg-[color:var(--surface)] w-14 h-8 text-xs" />
      </div>
      <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-3)]">
        <Switch checked={!!f.compact} onCheckedChange={(v) => set("compact", v)} />compact
      </label>
    </div>
  );
}

function MeasuresTab({ model, setModel, numericFields, datasetId }) {
  const measures = model.measures;
  const setMeasures = (arr) => setModel((m) => ({ ...m, measures: arr }));
  const update = (i, p) => setMeasures(measures.map((m, idx) => idx === i ? { ...m, ...p } : m));
  const remove = (i) => setMeasures(measures.filter((_, idx) => idx !== i));
  const addSimple = () => setMeasures([...measures, { key: uid("m"), label: "New measure", kind: "simple", field: numericFields[0]?.name || "", agg: "sum", visible: true, folder: "Measures", format: defFormat() }]);
  const addCalc = () => setMeasures([...measures, { key: uid("kpi"), label: "New KPI", kind: "calculated", expression: "", visible: true, folder: "KPIs", format: { ...defFormat(), compact: false, decimals: 2 } }]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[color:var(--ink-3)]">Define business measures &amp; calculated KPIs. Reference other measures in formulas with <code className="px-1 rounded bg-[color:var(--surface-2)]">[measure_key]</code>.</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={addSimple} data-testid="add-simple-measure"><Hash className="w-4 h-4 mr-1" />Measure</Button>
          <Button size="sm" onClick={addCalc} data-testid="add-calc-measure" className="bg-[color:var(--brand-mint)] hover:brightness-95 text-[color:var(--ink)]"><Calculator className="w-4 h-4 mr-1" />Calculated KPI</Button>
        </div>
      </div>
      <div className="space-y-3">
        {measures.map((m, i) => (
          <div key={m.key} data-testid="measure-row" className="glass-card p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] text-[color:var(--ink-3)]">Label</Label>
                  <Input value={m.label} onChange={(e) => update(i, { label: e.target.value })} className="mt-1 bg-[color:var(--surface)]" data-testid="measure-label" />
                  <div className="text-[10px] text-[color:var(--ink-3)] mt-1 font-mono">key: {m.key}</div>
                </div>
                {m.kind === "simple" ? (
                  <>
                    <div>
                      <Label className="text-[11px] text-[color:var(--ink-3)]">Field</Label>
                      <Select value={m.field || ""} onValueChange={(v) => update(i, { field: v })}>
                        <SelectTrigger className="mt-1 bg-[color:var(--surface)]"><SelectValue placeholder="field" /></SelectTrigger>
                        <SelectContent>{numericFields.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] text-[color:var(--ink-3)]">Aggregation</Label>
                      <Select value={m.agg || "sum"} onValueChange={(v) => update(i, { agg: v })}>
                        <SelectTrigger className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                        <SelectContent>{AGG_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <div className="md:col-span-2">
                    <Label className="text-[11px] text-[color:var(--ink-3)]">Formula</Label>
                    <CalcExpression value={m.expression || ""} onChange={(v) => update(i, { expression: v })} datasetId={datasetId} measures={measures} />
                  </div>
                )}
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-[color:var(--danger)] shrink-0" onClick={() => remove(i)} data-testid="measure-delete"><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Separator className="my-3" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <FormatEditor format={m.format} onChange={(f) => update(i, { format: f })} />
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-[color:var(--ink-3)]">{m.kind === "calculated" ? "Calculated" : "Simple"}</Badge>
                <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-3)]"><Switch checked={m.visible !== false} onCheckedChange={(v) => update(i, { visible: v })} />visible</label>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CalcExpression({ value, onChange, datasetId, measures }) {
  const [state, setState] = useState({ status: null, msg: "" });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiDesc, setAiDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const validate = async () => {
    setState({ status: "loading", msg: "" });
    try {
      const { data } = await api.post(`/datasets/${datasetId}/model/validate-measure`, { expression: value });
      if (data.ok) setState({ status: "ok", msg: `Sample value: ${data.value?.toLocaleString(undefined, { maximumFractionDigits: 4 })}` });
      else setState({ status: "err", msg: data.error || "Invalid" });
    } catch (e) { setState({ status: "err", msg: e?.response?.data?.detail || "Validation failed" }); }
  };
  const genAI = async () => {
    if (!aiDesc.trim()) { toast.error("Describe the formula first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-formula", { dataset_id: datasetId, description: aiDesc, mode: "measure" });
      if (data.expression) {
        onChange(data.expression);
        setState(data.validated
          ? { status: "ok", msg: `AI-generated & validated${data.sample != null ? ` — sample: ${Number(data.sample).toLocaleString(undefined, { maximumFractionDigits: 4 })}` : ""}` }
          : { status: "err", msg: data.error || "Generated, but failed validation — review it" });
        setAiOpen(false); setAiDesc("");
      } else toast.error(data.error || "AI returned no formula");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI formula generation failed"); }
    finally { setAiBusy(false); }
  };
  const simpleKeys = measures.filter((m) => m.kind === "simple").map((m) => m.key);
  return (
    <div className="mt-1">
      <Textarea value={value} onChange={(e) => { onChange(e.target.value); setState({ status: null, msg: "" }); }} rows={2} placeholder="e.g. [profit] / [revenue]" className="bg-[color:var(--surface)] font-mono text-sm" data-testid="measure-expression" />
      <div className="flex items-center justify-between mt-1.5 gap-2">
        <div className="flex flex-wrap gap-1">
          {simpleKeys.slice(0, 6).map((k) => (
            <button key={k} type="button" onClick={() => onChange((value || "") + `[${k}]`)} className="text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--surface-2)] text-[color:var(--ink-2)] hover:bg-[color:var(--accent)] font-mono">[{k}]</button>
          ))}
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="bg-[color:var(--surface)] h-7" onClick={() => setAiOpen((v) => !v)} data-testid="ai-formula-btn"><Wand2 className="w-3.5 h-3.5 mr-1 text-[color:var(--brand-mint)]" />AI</Button>
          <Button size="sm" variant="outline" className="bg-[color:var(--surface)] h-7" onClick={validate} data-testid="validate-measure-btn"><PlayCircle className="w-3.5 h-3.5 mr-1" />Validate</Button>
        </div>
      </div>
      {aiOpen && (
        <div className="flex gap-1.5 mt-1.5" data-testid="ai-formula-form">
          <Input value={aiDesc} onChange={(e) => setAiDesc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && genAI()}
            placeholder="Describe it: e.g. profit margin as a percentage of revenue" className="bg-[color:var(--surface)] h-8 text-xs" data-testid="ai-formula-desc" />
          <Button size="sm" className="h-8 bg-[color:var(--brand-mint)] hover:brightness-95 text-white" onClick={genAI} disabled={aiBusy} data-testid="ai-formula-generate">
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Generate"}
          </Button>
        </div>
      )}
      {state.status && state.status !== "loading" && (
        <div data-testid="validate-result" className={"flex items-center gap-1.5 text-[11px] mt-1.5 " + (state.status === "ok" ? "text-[color:var(--brand-mint)]" : "text-[color:var(--danger)]")}>
          {state.status === "ok" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}{state.msg}
        </div>
      )}
    </div>
  );
}

function DimensionsTab({ model, setModel, schema }) {
  const dims = model.dimensions;
  const setDims = (arr) => setModel((m) => ({ ...m, dimensions: arr }));
  const update = (i, p) => setDims(dims.map((d, idx) => idx === i ? { ...d, ...p } : d));
  const remove = (i) => setDims(dims.filter((_, idx) => idx !== i));
  const usedFields = new Set(dims.map((d) => d.field));
  const addable = schema.filter((c) => !usedFields.has(c.name));
  const add = () => { const c = addable[0]; if (!c) { toast.message("All fields are already added"); return; } setDims([...dims, { key: uid("d"), field: c.name, label: c.name.replace(/_/g, " "), data_type: c.type, folder: "Dimensions", visible: true, synonyms: [] }]); };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[color:var(--ink-3)]">Business-friendly dimensions used for grouping, filtering &amp; drilling.</p>
        <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={add} data-testid="add-dimension"><Plus className="w-4 h-4 mr-1" />Dimension</Button>
      </div>
      <div className="space-y-3">
        {dims.map((d, i) => (
          <div key={d.key} data-testid="dimension-row" className="glass-card p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start">
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Label</Label>
              <Input value={d.label} onChange={(e) => update(i, { label: e.target.value })} className="mt-1 bg-[color:var(--surface)]" data-testid="dimension-label" />
            </div>
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Field</Label>
              <div className="mt-1 h-10 flex items-center px-3 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] text-sm text-[color:var(--ink-2)] font-mono">{d.field}<Badge variant="outline" className="ml-auto text-[10px] text-[color:var(--ink-3)]">{d.data_type}</Badge></div>
            </div>
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Folder / Synonyms</Label>
              <Input value={(d.synonyms || []).join(", ")} onChange={(e) => update(i, { synonyms: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="search synonyms (comma)" className="mt-1 bg-[color:var(--surface)]" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <Button size="icon" variant="ghost" className="h-8 w-8 text-[color:var(--danger)]" onClick={() => remove(i)} data-testid="dimension-delete"><Trash2 className="w-4 h-4" /></Button>
              <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-3)]"><Switch checked={d.visible !== false} onCheckedChange={(v) => update(i, { visible: v })} />visible</label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HierarchiesTab({ model, setModel }) {
  const hers = model.hierarchies;
  const dims = model.dimensions;
  const setHers = (arr) => setModel((m) => ({ ...m, hierarchies: arr }));
  const update = (i, p) => setHers(hers.map((h, idx) => idx === i ? { ...h, ...p } : h));
  const remove = (i) => setHers(hers.filter((_, idx) => idx !== i));
  const add = () => setHers([...hers, { key: uid("h"), label: "New hierarchy", levels: [] }]);
  const dimLabel = (k) => dims.find((d) => d.key === k)?.label || k;
  const available = (h) => dims.filter((d) => !h.levels.includes(d.key));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[color:var(--ink-3)]">Ordered drill paths (e.g. Region → State → City).</p>
        <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={add} data-testid="add-hierarchy"><Plus className="w-4 h-4 mr-1" />Hierarchy</Button>
      </div>
      <div className="space-y-3">
        {hers.map((h, i) => (
          <div key={h.key} data-testid="hierarchy-row" className="glass-card p-4">
            <div className="flex items-center gap-3 mb-3">
              <Input value={h.label} onChange={(e) => update(i, { label: e.target.value })} className="bg-[color:var(--surface)] max-w-xs" data-testid="hierarchy-label" />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-[color:var(--danger)] ml-auto" onClick={() => remove(i)} data-testid="hierarchy-delete"><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {h.levels.map((lvl, li) => (
                <React.Fragment key={lvl}>
                  <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[color:var(--accent)] text-[color:var(--ink-2)] text-xs">
                    <GripVertical className="w-3 h-3 opacity-50" />{dimLabel(lvl)}
                    <button onClick={() => update(i, { levels: h.levels.filter((x) => x !== lvl) })} className="ml-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">×</button>
                  </span>
                  {li < h.levels.length - 1 && <span className="text-[color:var(--ink-3)]">→</span>}
                </React.Fragment>
              ))}
              {available(h).length > 0 && (
                <Select value="" onValueChange={(v) => update(i, { levels: [...h.levels, v] })}>
                  <SelectTrigger className="bg-[color:var(--surface)] w-[160px] h-8 text-xs border-dashed" data-testid="hierarchy-add-level"><SelectValue placeholder="+ add level" /></SelectTrigger>
                  <SelectContent>{available(h).map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecurityTab({ model, setModel, schema }) {
  const rules = model.rls_rules;
  const setRules = (arr) => setModel((m) => ({ ...m, rls_rules: arr }));
  const update = (i, p) => setRules(rules.map((r, idx) => idx === i ? { ...r, ...p } : r));
  const remove = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const add = () => setRules([...rules, { key: uid("rls"), label: "New rule", role: "member", field: schema[0]?.name || "", operator: "in", value_source: "static", values: [], user_attribute: "" }]);
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[color:var(--ink-3)]">Row-level security: restrict which rows a role can see. Applied automatically to every governed query.</p>
        <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={add} data-testid="add-rls-rule"><Plus className="w-4 h-4 mr-1" />RLS rule</Button>
      </div>
      <div className="space-y-3">
        {rules.length === 0 && <div className="glass-card p-6 text-center text-sm text-[color:var(--ink-3)]">No rules — all roles see all rows.</div>}
        {rules.map((r, i) => (
          <div key={r.key} data-testid="rls-row" className="glass-card p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1.4fr_auto] gap-3 items-end">
            <div><Label className="text-[11px] text-[color:var(--ink-3)]">Applies to role</Label>
              <Select value={r.role} onValueChange={(v) => update(i, { role: v })}><SelectTrigger className="mt-1 bg-[color:var(--surface)]" data-testid="rls-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-[11px] text-[color:var(--ink-3)]">Field</Label>
              <Select value={r.field} onValueChange={(v) => update(i, { field: v })}><SelectTrigger className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{schema.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-[11px] text-[color:var(--ink-3)]">Operator</Label>
              <Select value={r.operator} onValueChange={(v) => update(i, { operator: v })}><SelectTrigger className="mt-1 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>{OPS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">{r.value_source === "user_attribute" ? "User attribute" : "Values (comma)"}</Label>
              <div className="flex gap-1.5 mt-1">
                {r.value_source === "user_attribute"
                  ? <Input value={r.user_attribute || ""} onChange={(e) => update(i, { user_attribute: e.target.value })} placeholder="e.g. region" className="bg-[color:var(--surface)]" />
                  : <Input value={(r.values || []).join(", ")} onChange={(e) => update(i, { values: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="North, East" className="bg-[color:var(--surface)]" data-testid="rls-values" />}
                <Select value={r.value_source} onValueChange={(v) => update(i, { value_source: v })}><SelectTrigger className="bg-[color:var(--surface)] w-[60px] px-2"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="static">Static</SelectItem><SelectItem value="user_attribute">User</SelectItem></SelectContent></Select>
              </div>
            </div>
            <Button size="icon" variant="ghost" className="h-9 w-9 text-[color:var(--danger)]" onClick={() => remove(i)} data-testid="rls-delete"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <ClsSection model={model} setModel={setModel} schema={schema} />
    </div>
  );
}

const CLS_ROLES = [{ value: "*", label: "All roles" }, { value: "viewer", label: "Viewer" }, { value: "member", label: "Member" }, { value: "designer", label: "Designer" }, { value: "admin", label: "Admin" }];

function ClsSection({ model, setModel, schema }) {
  const rules = model.cls_rules || [];
  const setRules = (arr) => setModel((m) => ({ ...m, cls_rules: arr }));
  const update = (i, p) => setRules(rules.map((r, idx) => idx === i ? { ...r, ...p } : r));
  const remove = (i) => setRules(rules.filter((_, idx) => idx !== i));
  const add = () => setRules([...rules, { key: uid("cls"), label: "Hidden columns", role: "*", fields: [] }]);
  const toggleField = (i, f) => {
    const cur = rules[i].fields || [];
    update(i, { fields: cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f] });
  };
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[color:var(--ink-3)]"><b className="text-[color:var(--ink-2)]">Column-level security:</b> hide sensitive columns from a role. Hidden fields are stripped from every governed query, field list and filter for that role.</p>
        <Button size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={add} data-testid="add-cls-rule"><Plus className="w-4 h-4 mr-1" />CLS rule</Button>
      </div>
      <div className="space-y-3">
        {rules.length === 0 && <div className="glass-card p-5 text-center text-sm text-[color:var(--ink-3)]">No column rules — every role can query all columns.</div>}
        {rules.map((r, i) => (
          <div key={r.key} data-testid="cls-row" className="glass-card p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <EyeOff className="w-4 h-4 text-[color:var(--danger)] shrink-0" />
              <div className="w-44">
                <Label className="text-[11px] text-[color:var(--ink-3)]">Applies to role</Label>
                <Select value={r.role || "*"} onValueChange={(v) => update(i, { role: v })}>
                  <SelectTrigger className="mt-1 bg-[color:var(--surface)]" data-testid="cls-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLS_ROLES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[240px]">
                <Label className="text-[11px] text-[color:var(--ink-3)]">Hidden columns ({(r.fields || []).length})</Label>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {schema.map((c) => (
                    <button key={c.name} type="button" data-testid="cls-field-chip" onClick={() => toggleField(i, c.name)}
                      className={"text-[10px] px-2 py-0.5 rounded-full border transition-colors " + ((r.fields || []).includes(c.name)
                        ? "bg-[color:var(--danger)] text-white border-transparent"
                        : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-[color:var(--glass-border)] hover:text-[color:var(--ink)]")}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <Button size="icon" variant="ghost" className="h-9 w-9 text-[color:var(--danger)] shrink-0" onClick={() => remove(i)} data-testid="cls-delete"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewTab({ model, datasetId }) {
  const [selDims, setSelDims] = useState([]);
  const [selMeas, setSelMeas] = useState([]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const toggle = (arr, set, k) => set(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  const run = async () => {
    if (!selMeas.length) { toast.error("Pick at least one measure"); return; }
    setRunning(true);
    try {
      const { data } = await api.post(`/datasets/${datasetId}/model/query`, {
        dimensions: selDims.map((k) => ({ key: k })), measures: selMeas, limit: 100,
      });
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Query failed"); }
    finally { setRunning(false); }
  };

  const fmtFor = (key) => (result?.meta?.measures || []).find((m) => m.key === key)?.format;
  const labelFor = (key) => {
    const md = (result?.meta?.dimensions || []).find((d) => d.key === key);
    const mm = (result?.meta?.measures || []).find((m) => m.key === key);
    return md?.label || mm?.label || key;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
      <div className="glass-card p-4 space-y-4">
        <div>
          <Label className="text-xs text-[color:var(--ink-2)] font-semibold">Dimensions</Label>
          <div className="mt-2 space-y-1.5 max-h-44 overflow-auto pr-1">
            {model.dimensions.map((d) => (
              <label key={d.key} className="flex items-center gap-2 text-sm text-[color:var(--ink-2)] cursor-pointer">
                <input type="checkbox" checked={selDims.includes(d.key)} onChange={() => toggle(selDims, setSelDims, d.key)} data-testid="preview-dim" className="accent-[color:var(--brand-ocean)]" />{d.label}
              </label>
            ))}
          </div>
        </div>
        <Separator />
        <div>
          <Label className="text-xs text-[color:var(--ink-2)] font-semibold">Measures</Label>
          <div className="mt-2 space-y-1.5 max-h-44 overflow-auto pr-1">
            {model.measures.map((m) => (
              <label key={m.key} className="flex items-center gap-2 text-sm text-[color:var(--ink-2)] cursor-pointer">
                <input type="checkbox" checked={selMeas.includes(m.key)} onChange={() => toggle(selMeas, setSelMeas, m.key)} data-testid="preview-measure" className="accent-[color:var(--brand-mint)]" />
                {m.label}{m.kind === "calculated" && <Calculator className="w-3 h-3 text-[color:var(--ink-3)]" />}
              </label>
            ))}
          </div>
        </div>
        <Button onClick={run} disabled={running} className="w-full bg-[color:var(--brand-ocean)] text-white" data-testid="preview-run-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PlayCircle className="w-4 h-4 mr-1" />}Run query
        </Button>
      </div>

      <div className="glass-card p-4 overflow-auto">
        {!result ? (
          <div className="h-48 flex items-center justify-center text-sm text-[color:var(--ink-3)]">Pick dimensions &amp; measures, then run.</div>
        ) : !result.rows.length ? (
          <div className="h-48 flex items-center justify-center text-sm text-[color:var(--ink-3)]">No rows.</div>
        ) : (
          <table className="w-full text-sm" data-testid="preview-table">
            <thead>
              <tr className="text-left border-b border-[color:var(--glass-border)]">
                {result.columns.map((c) => <th key={c} className="py-2 px-3 font-medium text-[color:var(--ink-2)]">{labelFor(c)}</th>)}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-[color:var(--glass-border)] last:border-0 hover:bg-[color:var(--surface-2)]">
                  {result.columns.map((c) => {
                    const f = fmtFor(c);
                    return <td key={c} className="py-2 px-3 text-[color:var(--ink)] tabular-nums">{f ? formatValue(row[c], f) : String(row[c] ?? "\u2014")}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {result && <div className="text-[11px] text-[color:var(--ink-3)] mt-3">Engine: {result.engine} · {result.rows.length} rows</div>}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// JoinsTab - manage cross-source join contracts that involve this dataset
// ---------------------------------------------------------------------------
function JoinsTab({ datasetId, datasetSchema }) {
  const [contracts, setContracts] = useState([]);
  const [otherDatasets, setOtherDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [otherId, setOtherId] = useState("");
  const [keys, setKeys] = useState([{ left_field: "", right_field: "" }]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: cs }, { data: ds }] = await Promise.all([
        api.get(`/join-contracts?dataset_id=${datasetId}`),
        api.get("/datasets"),
      ]);
      setContracts(cs || []);
      setOtherDatasets((ds || []).filter((x) => x.id !== datasetId));
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [datasetId]);

  const otherSchema = (otherDatasets.find((d) => d.id === otherId)?.schema || []).map((c) => c.name);

  const save = async () => {
    const validKeys = keys.filter((k) => k.left_field && k.right_field);
    if (!name.trim() || !otherId || validKeys.length === 0) {
      toast.error("Fill name + other dataset + at least one complete key pair");
      return;
    }
    setSaving(true);
    try {
      await api.post("/join-contracts", {
        name: name.trim(),
        left_dataset_id: datasetId,
        right_dataset_id: otherId,
        keys: validKeys,
      });
      toast.success("Contract saved");
      setName(""); setOtherId(""); setKeys([{ left_field: "", right_field: "" }]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/join-contracts/${id}`);
      setContracts((cur) => cur.filter((c) => c.id !== id));
      toast.success("Contract deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" /></div>;

  const thisCols = (datasetSchema || []).map((c) => c.name);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-4">
        <div className="text-sm font-display text-[color:var(--ink)] flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-[color:var(--brand-ocean)]" />Add join contract
        </div>
        <p className="text-xs text-[color:var(--ink-3)] mb-3">Declare how this dataset can be joined to another. Composite (multi-column) keys are supported. Federated widgets and the AI planner will use it as a hard constraint.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
          <div>
            <Label className="text-[10px] text-[color:var(--ink-3)]">Contract name</Label>
            <Input data-testid="joins-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. CRM leads ↔ ERP orders" className="mt-1" />
          </div>
          <div>
            <Label className="text-[10px] text-[color:var(--ink-3)]">Other dataset</Label>
            <Select value={otherId} onValueChange={(v) => { setOtherId(v); setKeys([{ left_field: "", right_field: "" }]); }}>
              <SelectTrigger data-testid="joins-other" className="mt-1"><SelectValue placeholder="Pick a dataset…" /></SelectTrigger>
              <SelectContent>
                {otherDatasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <KeyPairsEditor
          value={keys}
          onChange={setKeys}
          leftColumns={thisCols}
          rightColumns={otherSchema}
          leftLabel="This.field"
          rightLabel="Other.field"
          testIdPrefix="joins"
        />
        <div className="mt-3">
          <Button data-testid="joins-save" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] text-white hover:bg-[#0a6f9c]">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Save contract
          </Button>
        </div>
      </div>

      {contracts.length === 0 ? (
        <div className="text-sm text-[color:var(--ink-3)] italic px-1">No join contracts yet.</div>
      ) : (
        <div className="rounded-xl border border-[color:var(--glass-border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-2)] text-[color:var(--ink-3)] text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Contract</th>
                <th className="px-3 py-2 font-medium">Direction</th>
                <th className="px-3 py-2 font-medium">Keys</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => {
                const isLeft = c.left_dataset_id === datasetId;
                const otherName = otherDatasets.find((d) => d.id === (isLeft ? c.right_dataset_id : c.left_dataset_id))?.name || "?";
                return (
                  <tr key={c.id} className="border-t border-[color:var(--glass-border)]">
                    <td className="px-3 py-2 text-[color:var(--ink-2)]">{c.name}</td>
                    <td className="px-3 py-2 text-xs text-[color:var(--ink-3)]">{isLeft ? "→" : "←"} {otherName}</td>
                    <td className="px-3 py-2 text-xs font-mono text-[color:var(--ink-2)]">
                      {c.keys.map((k) => `${k.left_field} = ${k.right_field}`).join("  AND  ")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button data-testid={`joins-del-${c.id}`} size="icon" variant="ghost" onClick={() => remove(c.id)} className="text-[color:var(--danger)]">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
